import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { rupee, apiError } from "@/lib/api";
import CustomerLayout from "@/components/CustomerLayout";
import { Spinner } from "@/components/States";
import { CheckCircle2, Circle, Clock, MapPin, Phone, KeyRound, Package, Store, Bike, Star, MessageSquare } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { key: "placed", label: "Order placed", testid: "order-timeline-step-received" },
  { key: "accepted", label: "Retailer accepted" },
  { key: "preparing", label: "Preparing order", testid: "order-timeline-step-packed" },
  { key: "ready", label: "Ready for pickup" },
  { key: "assigned", label: "Delivery partner assigned" },
  { key: "picked_up", label: "Picked up", testid: "order-timeline-step-dispatched" },
  { key: "out_for_delivery", label: "Out for delivery" },
  { key: "delivered", label: "Delivered", testid: "order-timeline-step-delivered" },
];
const ORDER = STEPS.map((s) => s.key);

export default function OrderTracking() {
  const { id } = useParams();
  const [refetchMs] = useState(5000);
  const { data: o, isLoading } = useQuery({
    queryKey: ["order", id], queryFn: () => api.get(`/orders/${id}`).then((r) => r.data),
    refetchInterval: (q) => (q.state.data?.status === "delivered" || q.state.data?.status === "cancelled" ? false : refetchMs),
  });

  if (isLoading || !o) return <CustomerLayout><div className="flex justify-center py-20"><Spinner className="h-8 w-8" /></div></CustomerLayout>;

  const currentIdx = ORDER.indexOf(o.status);
  const cancelled = o.status === "cancelled";

  return (
    <CustomerLayout>
      <div className="mx-auto max-w-2xl">
        <div className="mb-5 flex items-center justify-between rounded-2xl bg-brand-deep grain p-5 text-white">
          <div>
            <p className="text-xs text-emerald-200">Order</p>
            <h1 className="font-head text-2xl font-extrabold">#{o.order_no}</h1>
            <p className="mt-1 text-sm text-emerald-100">{o.store_name}</p>
          </div>
          {!cancelled && o.status !== "delivered" && (
            <div className="text-right">
              <p className="flex items-center gap-1 text-xs text-emerald-200"><Clock className="h-3.5 w-3.5" /> Arriving in</p>
              <p data-testid="order-eta-countdown" className="font-head text-3xl font-black">{o.eta_min}<span className="text-sm"> min</span></p>
            </div>
          )}
        </div>

        {cancelled ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center text-red-600">This order was cancelled.</div>
        ) : (
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <div className="relative space-y-5 before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100">
              {STEPS.map((s, i) => {
                const done = i <= currentIdx;
                const current = i === currentIdx;
                return (
                  <div key={s.key} data-testid={s.testid} className="relative flex items-center gap-4">
                    <div className={`relative z-10 ${current ? "text-brand live-dot rounded-full" : ""}`}>
                      {done ? <CheckCircle2 className="h-6 w-6 text-brand" /> : <Circle className="h-6 w-6 text-slate-300" />}
                    </div>
                    <span className={`text-sm font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Rate this order */}
        {o.status === "delivered" && <ReviewCard order={o} />}

        {/* Delivery OTP */}
        {["assigned", "picked_up", "out_for_delivery"].includes(o.status) && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-soft p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-amber-warm"><KeyRound className="h-4 w-4" /> Share this OTP with the delivery partner</p>
            <p data-testid="order-delivery-otp-badge" className="mt-2 font-mono text-4xl font-black tracking-[0.3em] text-amber-warm">{o.delivery_otp}</p>
          </div>
        )}

        {/* Delivery partner */}
        {o.delivery_partner && (
          <div data-testid="order-partner-card" className="mt-4 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-soft text-amber-warm"><Bike className="h-6 w-6" /></div>
            <div className="flex-1"><p className="font-semibold text-slate-800">{o.delivery_partner.name}</p><p className="text-xs text-slate-500">Your delivery partner</p></div>
            <a data-testid="order-partner-call-btn" href={`tel:${o.delivery_partner.phone}`} className="flex h-10 w-10 items-center justify-center rounded-full bg-brand text-white"><Phone className="h-4 w-4" /></a>
          </div>
        )}

        {/* Items & bill */}
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
          <h3 className="mb-3 flex items-center gap-2 font-head font-bold text-slate-900"><Package className="h-4 w-4 text-brand" /> Order details</h3>
          <div className="space-y-2">
            {o.items.map((it) => (
              <div key={it.product_id} className="flex items-center justify-between text-sm">
                <span className="text-slate-700">{it.name} <span className="text-slate-400">× {it.qty}</span></span>
                <span className="font-mono text-slate-800">{rupee(it.price * it.qty)}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-1 border-t border-dashed border-slate-200 pt-3 text-sm">
            <Row label="Subtotal" value={rupee(o.subtotal)} />
            <Row label="Delivery + Platform fee" value={rupee(o.delivery_fee + o.platform_fee)} />
            <Row label="Taxes" value={rupee(o.tax)} />
            {o.discount > 0 && <Row label="Discount" value={`- ${rupee(o.discount)}`} />}
            <div className="flex items-center justify-between border-t border-slate-100 pt-1 font-head font-bold">
              <span>Total ({o.payment_method.toUpperCase()})</span><span className="font-mono text-brand">{rupee(o.total)}</span>
            </div>
          </div>
          <p className="mt-3 flex items-start gap-1.5 text-xs text-slate-500"><MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {o.address?.line}, {o.address?.city} {o.address?.pincode}</p>
        </div>
      </div>
    </CustomerLayout>
  );
}

const Row = ({ label, value }) => (<div className="flex items-center justify-between text-slate-600"><span>{label}</span><span className="font-mono">{value}</span></div>);

function ReviewCard({ order }) {
  const qc = useQueryClient();
  const existing = order.review;
  const [rating, setRating] = useState(existing?.rating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating) return toast.error("Please pick a star rating");
    setBusy(true);
    try {
      await api.post("/reviews", { store_id: order.store_id, order_id: order.id, rating, comment });
      toast.success("Thanks for rating " + order.store_name + "!");
      qc.invalidateQueries({ queryKey: ["order", order.id] });
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  if (existing) {
    return (
      <div data-testid="order-review-submitted" className="mt-4 rounded-2xl border border-brand/20 bg-brand-soft/40 p-5">
        <p className="flex items-center gap-2 text-sm font-bold text-brand"><MessageSquare className="h-4 w-4" /> Your review</p>
        <div className="mt-2 flex gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => <Star key={i} className={`h-5 w-5 ${i < existing.rating ? "fill-amber-surge text-amber-surge" : "text-slate-300"}`} />)}
        </div>
        {existing.comment && <p className="mt-2 text-sm text-slate-600">“{existing.comment}”</p>}
      </div>
    );
  }

  return (
    <div data-testid="order-review-card" className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="font-head font-bold text-slate-900">Rate your order</h3>
      <p className="mt-0.5 text-sm text-slate-500">How was your experience with {order.store_name}?</p>
      <div className="mt-3 flex gap-1" data-testid="order-review-stars">
        {Array.from({ length: 5 }).map((_, i) => {
          const val = i + 1;
          return (
            <button key={i} type="button" data-testid={`review-star-${val}`}
              onMouseEnter={() => setHover(val)} onMouseLeave={() => setHover(0)} onClick={() => setRating(val)}
              className="transition-transform hover:scale-110 active:scale-95">
              <Star className={`h-8 w-8 ${(hover || rating) >= val ? "fill-amber-surge text-amber-surge" : "text-slate-300"}`} />
            </button>
          );
        })}
      </div>
      <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} data-testid="order-review-comment"
        placeholder="Share a few words (optional)…" className="mt-3 w-full resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-brand" />
      <button onClick={submit} disabled={busy} data-testid="order-review-submit"
        className="mt-2 w-full rounded-xl bg-brand py-2.5 text-sm font-bold text-white hover:bg-brand-deep disabled:opacity-60">
        {busy ? "Submitting…" : "Submit review"}
      </button>
    </div>
  );
}
